/**
 * <wrb-gallery> — wires prev/next arrows and dot indicators to a native scroll-snap track.
 * Scrolling itself stays native (touch momentum, trackpads, keyboard focus into cards).
 */
const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

class WrbGallery extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('.wrb-gallery__track');
    if (!this.track) return;
    this.items = [...this.track.children];
    this.arrows = [...this.querySelectorAll('.wrb-gallery__arrow')];
    this.dots = [...this.querySelectorAll('.wrb-gallery__dot')];

    this.arrows.forEach((arrow) =>
      arrow.addEventListener('click', () => this.#step(Number(arrow.dataset.dir)))
    );
    this.dots.forEach((dot) => dot.addEventListener('click', () => this.#goTo(Number(dot.dataset.index))));

    this.track.addEventListener('scroll', this.#onScroll, { passive: true });
    window.addEventListener('resize', this.#onScroll, { passive: true });
    this.#update();
  }

  disconnectedCallback() {
    this.track?.removeEventListener('scroll', this.#onScroll);
    window.removeEventListener('resize', this.#onScroll);
  }

  get #behavior() {
    return reduceMotion.matches ? 'auto' : 'smooth';
  }

  #itemLeft(index) {
    const first = this.items[0];
    return this.items[index].offsetLeft - (first ? first.offsetLeft : 0);
  }

  #currentIndex() {
    const x = this.track.scrollLeft;
    let best = 0;
    this.items.forEach((_, i) => {
      if (Math.abs(this.#itemLeft(i) - x) < Math.abs(this.#itemLeft(best) - x)) best = i;
    });
    return best;
  }

  #goTo(index) {
    const i = Math.max(0, Math.min(index, this.items.length - 1));
    this.track.scrollTo({ left: this.#itemLeft(i), behavior: this.#behavior });
  }

  #step(dir) {
    this.#goTo(this.#currentIndex() + dir);
  }

  #frame = 0;
  #onScroll = () => {
    cancelAnimationFrame(this.#frame);
    this.#frame = requestAnimationFrame(() => this.#update());
  };

  #update() {
    const max = this.track.scrollWidth - this.track.clientWidth;
    const atStart = this.track.scrollLeft <= 2;
    const atEnd = this.track.scrollLeft >= max - 2;
    const [prev, next] = this.arrows;
    if (prev) prev.disabled = atStart;
    if (next) next.disabled = atEnd || max <= 0;

    // At the far end the last cards can't snap to the left edge, so mark the last dot active.
    const current = atEnd && max > 0 ? this.items.length - 1 : this.#currentIndex();
    this.dots.forEach((dot, i) => {
      if (i === current) dot.setAttribute('aria-current', 'true');
      else dot.removeAttribute('aria-current');
    });
  }
}

if (!customElements.get('wrb-gallery')) customElements.define('wrb-gallery', WrbGallery);
